import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { UserPlus, User, CalendarClock, Phone, Mail, Trash2, AlertTriangle, X } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import InfoTip from "./InfoTip.jsx";
import CopyButton from "./CopyButton.jsx";

// Panel "Přivedené kamarádky" v detailu klienta (akce "Přiveď kamarádku", léto 2026 do 31.8.).
// Read-only přehled leadů, které tento klient přivedl (lead vyplnil v dotazníku jeho kód).
// Data z /api/client-referrals (čte leads.referrer_klient_id). Nezasahuje do lead pipeline.

function notionIdFromUrl(u) {
  const m = String(u || "").match(/([0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  return m ? m[1] : "";
}

function fmtDay(ds) {
  if (!ds) return "—";
  return String(ds).split("-").reverse().join(".");
}

export default function ClientReferralsSection({ klientId, klientNotion, klientName, bezRamce = false, onCount }) {
  const parentId = klientId || notionIdFromUrl(klientNotion);
  const [kamaradky, setKamaradky] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(null); // kamaradka k smazani | null
  const [deleting, setDeleting] = useState(false);
  // 9.9.2026 — kontakt je na telefonu ve dvou řádcích s kopírováním, akce jsou velké.

  const role = (getCurrentUser() || {}).role || "";
  const isOwner = role === "owner";

  useEffect(() => {
    let alive = true;
    if (!parentId) { setLoading(false); return; }
    (async () => {
      try {
        const r = await apiFetch(`/api/client-referrals?parentId=${encodeURIComponent(parentId)}`);
        const j = await r.json();
        if (alive) setKamaradky(Array.isArray(j.kamaradky) ? j.kamaradky : []);
      } catch (_) { /* ticho */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [parentId]);

  const doDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await apiFetch("/api/lead-delete", {
        method: "POST",
        body: JSON.stringify({ notionPageId: confirmDel.notion_id || undefined, email: confirmDel.email || undefined }),
      });
      setKamaradky((prev) => prev.filter((k) => k.id !== confirmDel.id));
      setConfirmDel(null);
    } catch (_) { /* ticho */ }
    setDeleting(false);
  };

  // Bez přivedených kamarádek panel vůbec nezobrazuj (ať detail nezahltíme prázdnou sekcí).
  // Odznak v hlavičce sekce; null znamená "sekci vůbec nezobrazuj" (9.9.2026).
  useEffect(() => {
    if (typeof onCount !== "function" || loading) return;
    onCount(kamaradky.length ? kamaradky.length : null);
  }, [kamaradky, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || kamaradky.length === 0) return null;

  return (
    <>
    <div className={bezRamce ? "" : "bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm"}>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {!bezRamce && <UserPlus className="w-5 h-5 text-rose-500" />}
        {!bezRamce && <h3 className="text-base font-semibold text-slate-900">Přivedené kamarádky</h3>}
        {!bezRamce && <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-xs font-bold text-white bg-rose-500 rounded-full">{kamaradky.length}</span>}
        <InfoTip text="Leady, které tato klientka přivedla v akci Přiveď kamarádku (do 31.8.2026). Každá klientka může přivést jen jednu kamarádku (po využití jí karta v aplikaci zmizí). Interní postup: 1) kamarádka vyplní dotazník, 2) zavoláme jí a domluvíme zkušební trénink, vede se jako spojený s klientkou, ALE trenér ho nemá zaplacený jako spojený, 3) po tréninku se jí ujme head coach senior a probere možnosti spolupráce." />
      </div>

      <div className="space-y-2.5">
        {kamaradky.map((k) => (
          <div key={k.id} className="rounded-xl border border-slate-200 bg-white p-3.5">
            <div className="flex items-center gap-3">
              <div className="hidden h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-500 sm:flex">
                <User className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-base font-bold text-slate-800">{k.jmeno || "Neznámý lead"}</span>
                  <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11.5px] font-bold text-slate-500">
                    <CalendarClock className="h-3.5 w-3.5" /> {fmtDay(k.datum)}
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {k.stav && <span className="inline-flex items-center rounded-full border border-plum-100 bg-plum-50 px-2 py-0.5 text-xs font-semibold text-plum-700">{k.stav}</span>}
                  {k.stage && <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{k.stage}</span>}
                </div>
              </div>
            </div>
            {/* Kontakt: celý telefon i e-mail, každý s tlačítkem zkopírovat (9.9.2026). */}
            {(k.telefon || k.email) && (
              <div className="mt-2.5 space-y-1.5">
                {k.telefon && (
                  <div className="flex min-h-[48px] items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
                    <Phone className="h-4 w-4 flex-shrink-0 text-slate-400" />
                    <a href={`tel:${String(k.telefon).replace(/\s/g, "")}`} className="min-w-0 flex-1 truncate text-[14px] font-semibold text-slate-800">{k.telefon}</a>
                    <CopyButton value={k.telefon} title="Zkopírovat telefon" className="!h-11 !w-11 !border-0" />
                  </div>
                )}
                {k.email && (
                  <div className="flex min-h-[48px] items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
                    <Mail className="h-4 w-4 flex-shrink-0 text-slate-400" />
                    <a href={`mailto:${k.email}`} className="min-w-0 flex-1 truncate text-[14px] font-semibold text-slate-800 demo-sensitive">{k.email}</a>
                    <CopyButton value={k.email} title="Zkopírovat e-mail" className="!h-11 !w-11 !border-0" />
                  </div>
                )}
              </div>
            )}
            {(k.telefon || isOwner) && (
              <div className="mt-2.5 flex items-center gap-2">
                {k.telefon && (
                  <a href={`tel:${String(k.telefon).replace(/\s/g, "")}`}
                    className="flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl bg-brand text-[14px] font-bold text-white hover:bg-brand-deep">
                    <Phone className="h-4 w-4" /> Zavolat
                  </a>
                )}
                {isOwner && (
                  <button
                    onClick={() => setConfirmDel(k)}
                    aria-label="Odstranit kamarádku"
                    title="Odstranit tento lead (jen owner)"
                    className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border-2 border-rose-200 text-rose-600 hover:bg-rose-50 ${k.telefon ? "" : "ml-auto"}`}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>

    {confirmDel && createPortal((
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-brand-dark/50 p-4 backdrop-blur-sm pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={() => !deleting && setConfirmDel(null)}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0"><AlertTriangle className="w-5 h-5" /></span>
              <h3 className="text-base font-bold text-slate-900">Odstranit kamarádku?</h3>
            </div>
            <button onClick={() => !deleting && setConfirmDel(null)} aria-label="Zavřít" className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-5">
            <p className="text-sm text-slate-600 leading-relaxed">
              Opravdu smazat lead <span className="font-semibold text-slate-800">{confirmDel.jmeno || "Neznámý lead"}</span>
              {confirmDel.email ? <span className="text-slate-500"> ({confirmDel.email})</span> : null}? Tím se odstraní z databáze i z Notion a klientce se znovu zobrazí možnost přivést kamarádku. Akce je nevratná.
            </p>
          </div>
          <div className="flex items-center gap-2 border-t border-slate-100 px-5 py-4">
            <button onClick={doDelete} disabled={deleting}
              className="btn btn-danger flex-1 sm:flex-none">
              <Trash2 className="h-4 w-4" /> {deleting ? "Mažu…" : "Odstranit"}
            </button>
            <button onClick={() => setConfirmDel(null)} disabled={deleting}
              className="flex min-h-[48px] flex-1 items-center justify-center rounded-xl border border-slate-300 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50 sm:flex-none sm:px-5">Zrušit</button>
          </div>
        </div>
      </div>
    ), document.body)}
    </>
  );
}
